import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, AlertCircle } from 'lucide-react';

interface ImageDropzoneProps {
  onUpload: (file: File) => Promise<void>;
  disabled?: boolean;
  maxFiles?: number;
  maxSize?: number;
}

interface PreviewFile extends File {
  preview: string;
}

const ImageDropzone: React.FC<ImageDropzoneProps> = ({
  onUpload,
  disabled = false,
  maxFiles = 1,
  maxSize = 5 * 1024 * 1024 // 5MB
}) => {
  const [files, setFiles] = useState<PreviewFile[]>([]);
  const [error, setError] = useState<string>('');
  const [uploading, setUploading] = useState(false);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    try {
      setError('');
      setUploading(true);

      // Validar número máximo de archivos
      if (acceptedFiles.length > maxFiles) {
        throw new Error(`Máximo ${maxFiles} archivos permitidos`);
      }

      // Validar tipo de archivo
      const file = acceptedFiles[0];
      if (!file.type.match(/^image\/(jpeg|png|jpg)$/)) {
        throw new Error('Formato de archivo no válido. Solo se permiten JPG y PNG.');
      }

      // Validar tamaño
      if (file.size > maxSize) {
        throw new Error('El archivo es demasiado grande. Máximo 5MB.');
      }

      // Crear previsualización
      const previewFile = Object.assign(file, {
        preview: URL.createObjectURL(file)
      });
      setFiles([previewFile]);

      // Subir archivo
      await onUpload(file);
      
      // Limpiar después de subir exitosamente
      setFiles([]);

    } catch (err: any) {
      setError(err.message || 'Error al subir la imagen');
      console.error('Error uploading:', err);
    } finally {
      setUploading(false);
    }
  }, [maxFiles, maxSize, onUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png']
    },
    maxSize,
    maxFiles,
    disabled: disabled || uploading,
    multiple: false
  });

  // Limpiar URLs de previsualización al desmontar
  useEffect(() => {
    return () => files.forEach(file => URL.revokeObjectURL(file.preview));
  }, [files]);

  const removeFile = (index: number) => {
    const file = files[index];
    if (file) {
      URL.revokeObjectURL(file.preview);
      setFiles(files => files.filter((_, i) => i !== index));
    }
  };

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors duration-300 ${
          disabled || uploading
            ? 'opacity-50 cursor-not-allowed'
            : isDragActive
            ? 'border-primary bg-primary/5'
            : 'border-white/10 hover:border-primary/50 cursor-pointer'
        }`}
      >
        <input {...getInputProps()} />
        <Upload
          className={`mx-auto h-12 w-12 mb-4 ${
            uploading ? 'text-gray-600 animate-bounce' : 'text-gray-400'
          }`}
        />
        {uploading ? (
          <p className="text-lg text-gray-300">Analizando por el Asistente...</p>
        ) : isDragActive ? (
          <p className="text-lg text-gray-300">Suelta la imagen aquí...</p>
        ) : (
          <div>
            <p className="text-lg text-gray-300 mb-2">
              {disabled 
                ? 'Has alcanzado el límite diario de análisis gratuitos'
                : 'Arrastra y suelta una imagen aquí, o haz clic para seleccionar'}
            </p>
            <p className="text-sm text-gray-500">
              PNG o JPG (máx. 5MB)
            </p>
          </div>
        )}
      </div>

      {/* Mensajes de error */}
      {error && (
        <div className="bg-red-900/20 border border-red-900/50 rounded-lg p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
          <p className="text-red-500">{error}</p>
        </div>
      )}

      {/* Vista previa de imagen */}
      {files.length > 0 && (
        <div className="relative">
          <img
            src={files[0].preview}
            alt="Vista previa"
            className="w-full h-64 object-contain rounded-lg bg-dark-overlay"
          />
          <button
            onClick={() => removeFile(0)}
            className="absolute top-2 right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ImageDropzone;